/**
 * /!\ This file is auto-generated.
 *
 * This is the entry point of your standalone application.
 *
 * There are multiple tags used by the editor to inject code automatically:
 *     - `wle:auto-imports:start` and `wle:auto-imports:end`: The list of import statements
 *     - `wle:auto-register:start` and `wle:auto-register:end`: The list of component to register
 *     - `wle:auto-constants:start` and `wle:auto-constants:end`: The project's constants,
 *        such as the project's name, whether it should use the physx runtime, etc...
 *     - `wle:auto-benchmark:start` and `wle:auto-benchmark:end`: Append the benchmarking code
 */

import {loadRuntime} from '@wonderlandengine/api';

import {AppLixirAdProvider} from './js/utils/uber-sdk/advertisingAppLixir.js';
import {v4 as uuidv4} from 'uuid';
import {ErrorSeverity} from './js/utils/game-analytics-enums.js';
import {AdInPlayAdProvider} from './js/utils/uber-sdk/advertisingAdInPlay.js';

import * as Enemies from './index-enemies.js';
import * as Player from './index-player.js';
import * as Weapons from './index-weapons.js';

/* wle:auto-imports:start */
import {AudioListener} from '@wonderlandengine/spatial-audio';
import {BurstMeshParticles} from './js/components/burst-mesh-particles.js';
import {ControlEvents} from './js/components/control-events.js';
import {DamageTextParticles} from './js/components/damage-text-particles.js';
import {EnemyPrefabs} from './js/components/enemy-prefabs.js';
import {EnemySpawner} from './js/components/enemy-spawner.js';
import {FruitPowerVfxManager} from './js/components/fruit-power-vfx-manager.js';
import {GameState} from './js/components/game-state.js';
import {Hover} from './js/components/hover.js';
import {LookAt} from './js/components/look-at.js';
import {MessageController} from './js/components/message-controller.js';
import {MovePlayer} from './js/components/move-player.js';
import {NpcComponent} from './js/components/npc-component.js';
import {PlayerComponent} from './js/components/player-component.js';
import {Portal} from './js/components/portal.js';
import {PowerFruit} from './js/components/power-fruit.js';
import {QuestDescription} from './js/components/quest-description.js';
import {Region} from './js/components/region.js';
import {Rotate} from './js/components/rotate.js';
import {ScatterSpawn} from './js/components/scatter-spawn.js';
import {CircularSpawner} from './js/components/scatter.js';
import {ShopComponent} from './js/components/shop-component.js';
import {ThirdpersonCamera} from './js/components/thirdperson-camera.js';
import {WeaponPrefabs} from './js/components/weapon-prefabs.js';
import {ReactUi} from './js/ui/hud.tsx';
import {TouchUI} from './js/ui/touch-ui.js';
/* wle:auto-imports:end */
import {loadAudio} from './js/utils/Audio.js';

import {Constants as GameplayConstants} from './js/state/data/constants.js';
import {extra, ads, user, saveGame, analytics} from './js/utils/uber-sdk';
import {CrazyGamesProvider} from './js/utils/uber-sdk/crazygames.js';
import {CookieSaveGameProvider} from './js/utils/uber-sdk/savegame.js';
import {gameState} from './js/state/GameState.js';

let sdkPromise = null;
if (!WL_EDITOR) {
    const ga = require('gameanalytics');
    window.GameAnalytics = ga.GameAnalytics;
    GameAnalytics.setEnabledInfoLog(TARGET !== 'crazygames');
    const GA_GAME_KEY = 'b74721dd236bb8a09a1e2e1d4172de1a';
    const GA_SECRET_KEY = '26632ccc0435fb11179902acdef75e462ae796eb';
    GameAnalytics.configureAvailableResourceCurrencies(['coins']);
    GameAnalytics.initialize(GA_GAME_KEY, GA_SECRET_KEY);
    GameAnalytics.configureBuild(GA_BUILD_ID);

    ga.GameAnalytics.addRemoteConfigsListener({
        onRemoteConfigsUpdated: () => {
            /**
             * A/B Testing will allow us to remote-control variations of
             * the game and measure change in engangement, revenue and
             * other metrics.
             */
            const all = ga.GameAnalytics.getRemoteConfigsContentAsString('my_var');
            const configs = JSON.parse(all);
            console.log('Received remote config.');
            for (let [k, v] of Object.entries(configs)) {
                if (k in GameplayConstants) {
                    try {
                        if (typeof GameplayConstants[k] === 'number') {
                            v = Number.parseFloat(v);
                        }
                        GameplayConstants[k] = v;
                    } catch (e) {
                        GameAnalytics.addErrorEvent(
                            ErrorSeverity.Error,
                            `Unable to apply config "${k}" with value "${v}":` +
                                e.toString()
                        );
                    }
                }
            }
        },
    });

    if (TARGET === 'crazygames') {
        const cg = new CrazyGamesProvider();

        ads.registerProvider(cg);
        analytics.registerProvider(cg);
        saveGame.registerProvider(cg);
        extra.registerProvider(cg);
        user.registerProvider(cg);
        cg.onReady.then(() => {
            analytics.trackLoadingStart();
        });
        sdkPromise = cg.onReady;
    } else if (WITH_ADS) {
        const adinplay = new AdInPlayAdProvider({});
        ads.registerProvider(adinplay);

        const applixir = new AppLixirAdProvider({
            accountId: 8140,
            siteId: 8663,
            userId: uuidv4(),
        });
        ads.registerProvider(applixir);
    }

    saveGame.registerProvider(new CookieSaveGameProvider('power-fruits-save'));
}

/* wle:auto-constants:start */
const Constants = {
    ProjectName: 'PowerFruits',
    RuntimeBaseName: 'WonderlandRuntime',
    WebXRRequiredFeatures: ['local',],
    WebXROptionalFeatures: ['local','hand-tracking','hit-test',],
};
const RuntimeOptions = {
    physx: true,
    loader: false,
    xrFramebufferScaleFactor: 1,
    xrOfferSession: {
        mode: 'auto',
        features: Constants.WebXRRequiredFeatures,
        optionalFeatures: Constants.WebXROptionalFeatures,
    },
    canvas: 'canvas',
};
/* wle:auto-constants:end */

const engine = await loadRuntime(Constants.RuntimeBaseName, RuntimeOptions);
engine.erasePrototypeOnDestroy = true;

/* Make sure all providers are initialized before we load the game for example */
gameState.init();

gameState.onPause.add(() => {
    engine.wasm._wl_application_stop();
    analytics.trackGameplayStop();
});

gameState.onResume.add(() => {
    engine.start();
    analytics.trackGameplayStart();
});

// Load components
/* wle:auto-register:start */
engine.registerComponent(AudioListener);
engine.registerComponent(BurstMeshParticles);
engine.registerComponent(ControlEvents);
engine.registerComponent(DamageTextParticles);
engine.registerComponent(EnemyPrefabs);
engine.registerComponent(EnemySpawner);
engine.registerComponent(FruitPowerVfxManager);
engine.registerComponent(GameState);
engine.registerComponent(Hover);
engine.registerComponent(LookAt);
engine.registerComponent(MessageController);
engine.registerComponent(MovePlayer);
engine.registerComponent(NpcComponent);
engine.registerComponent(PlayerComponent);
engine.registerComponent(Portal);
engine.registerComponent(PowerFruit);
engine.registerComponent(QuestDescription);
engine.registerComponent(Region);
engine.registerComponent(Rotate);
engine.registerComponent(ScatterSpawn);
engine.registerComponent(CircularSpawner);
engine.registerComponent(ShopComponent);
engine.registerComponent(ThirdpersonCamera);
engine.registerComponent(WeaponPrefabs);
engine.registerComponent(ReactUi);
engine.registerComponent(TouchUI);
/* wle:auto-register:end */
Enemies.default(engine);
Player.default(engine);
Weapons.default(engine);

try {
    if (sdkPromise) await sdkPromise;
    await engine.loadMainScene(`${Constants.ProjectName}.bin`);
    analytics.trackLoadingStop();
    analytics.trackGameplayStart();
    loadAudio();
    const el = document.getElementById('version');
    if (el) setTimeout(() => el.remove(), 2000);
} catch (e) {
    analytics.trackLoadingStop();
    console.error(e);
    GameAnalytics.addErrorEvent(
        ErrorSeverity.Critical,
        `Failed to load ${Constants.ProjectName}.bin:` + e.toString()
    );
}

window.onerror = (e) => {
    GameAnalytics.addErrorEvent(ErrorSeverity.Error, `Uncaught exception:` + e.toString());
    /* Default handling for developers */
    return false;
};

/* wle:auto-benchmark:start */
/* wle:auto-benchmark:end */
